const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/config/liho/eCheckin/sections/Versicherung.tsx';
import * as React from 'react';
import { forwardRef } from 'react';
import { useIntl } from 'react-intl';
import { useCIS4m, useCIS4mData } from '@cistec/common_web/modules/CIS4m/hooks';
import { ECheckinSectionProps } from 'App/components/eCheckin/types';
import ECheckinSection from 'App/components/eCheckin/components/ECheckinSection';
import { createFieldPath } from 'App/components/eCheckin/utils';
import { VersicherungField } from 'App/components/eCheckin/components/fields/metakat/VersicherungField';
import textMap from '../translations';
import { CIS4mTextField } from '@cistec/common_web/modules/CIS4m/components/CIS4mTextField';
import _ from 'lodash';
import { CIS4mBlock } from '@cistec/common_web/modules/CIS4m/components/CIS4mBlock';
import { VersicherungsdeckungField } from 'App/components/eCheckin/components/fields/metakat/VersicherungsdeckungField';
import { CIS4mDatePicker } from '@cistec/common_web/modules/CIS4m/components/CIS4mDatePicker';
import { validateInsuranceNumber } from '../validation';
import { YesNoField } from 'App/components/eCheckin/components/fields/YesNoField';
import moment from 'moment';
import { UnfallversicherungsdeckungField } from 'App/components/eCheckin/components/fields/metakat/UnfallversicherungsdeckungField';
import { useText } from '@cistec/common_web/utils/intlUtils';
import { Divider } from '@mui/material';

export const Versicherung = forwardRef((props: ECheckinSectionProps, ref: any) => {
    const { path } = props;
    const title = useText(props.title);

    const PATH_VERSICHERUNG = createFieldPath(path, 'versicherung');
    const PATH_VERFUEGUNGSNR = createFieldPath(path, 'verfuegungsnr');
    const PATH_VERSICHERUNG_DECKUNG = createFieldPath(path, 'abdeckung');

    const PATH_VERSICHERUNG_NAME = createFieldPath(path, 'versicherungName');
    const PATH_VERSICHERUNG_CARDNR = createFieldPath(path, 'cardnr');
    const PATH_VERSICHERUNG_EXPIRY_DATE = createFieldPath(path, 'expirydate');

    const PATH_UNFALL_GEMELDET = createFieldPath(path, 'unfallGemeldet');
    const PATH_UNFALL_DATE = createFieldPath(path, 'unfallDatum');
    const PATH_UNFALL_NUMBER = createFieldPath(path, 'unfallNumber');

    const PATH_ZUSATZVERSICHERUNG_NAME = createFieldPath(path, 'zusatzverName');
    const PATH_ZUSATZVERSICHERUNG_CARDNR = createFieldPath(path, 'zusatzverCardnr');
    const PATH_ZUSATZVERSICHERUNG_EXPIRY_DATE = createFieldPath(path, 'zusatzverExpirydate');

    const { formatMessage } = useIntl();

    const eCheckinSectionRef = useCIS4m(ref);

    const formData = useCIS4mData();

    const versicherung = _.get(formData, PATH_VERSICHERUNG);
    const hideVerfuegungsNumber = !['disability', 'military'].includes(versicherung);

    const hideSickBasicBlock = !['sickbasic'].includes(versicherung);
    const hideUnfallBlock = !['accident'].includes(versicherung);
    const hideVersicherungsNameBlock = !['accident', 'sickbasic'].includes(versicherung);
    const hideCardInfoBlock = !['sickbasic'].includes(versicherung);

    const klasse = _.get(formData, PATH_VERSICHERUNG_DECKUNG);

    const hideZusatsversicherungBlock = !(
        ['accident', 'sickbasic'].includes(versicherung) && ['private', 'semi-private'].includes(klasse)
    );

    return (
        <ECheckinSection ref={eCheckinSectionRef} path={path} title={title}>
            <h1>{title}</h1>
            <VersicherungField label={textMap.versicherung} path={PATH_VERSICHERUNG} required={true} />
            <CIS4mTextField label={textMap.verfuegungsNr} path={PATH_VERFUEGUNGSNR} hidden={hideVerfuegungsNumber} />
            <CIS4mBlock hidden={hideSickBasicBlock}>
                {
                    // Because of sharing the same path (PATH_VERSICHERUNG_DECKUNG) only one of the components can be rendered at a time
                    hideSickBasicBlock ? null : (
                        <VersicherungsdeckungField
                            label={textMap.versicherungsdeckung}
                            path={PATH_VERSICHERUNG_DECKUNG}
                            required={true}
                        />
                    )
                }
            </CIS4mBlock>

            <CIS4mBlock hidden={hideVersicherungsNameBlock}>
                <CIS4mTextField label={textMap.nameDerVersicherung} path={PATH_VERSICHERUNG_NAME} />
            </CIS4mBlock>

            <CIS4mBlock hidden={hideUnfallBlock}>
                {
                    // Because of sharing the same path (PATH_VERSICHERUNG_DECKUNG) only one of the components can be rendered at a time
                    hideUnfallBlock ? null : (
                        <UnfallversicherungsdeckungField
                            label={textMap.versicherungsdeckung}
                            path={PATH_VERSICHERUNG_DECKUNG}
                            required={true}
                        />
                    )
                }
                <YesNoField label={textMap.unfallGemeldet} path={PATH_UNFALL_GEMELDET} />
                <CIS4mDatePicker
                    label={formatMessage(textMap.unfallDatum)}
                    path={PATH_UNFALL_DATE}
                    maxDate={moment()}
                    required={true}
                />
                <CIS4mTextField label={textMap.unfallNumber} path={PATH_UNFALL_NUMBER} required={true} />
            </CIS4mBlock>

            <CIS4mBlock hidden={hideCardInfoBlock}>
                <CIS4mTextField
                    label={textMap.cardnr}
                    path={PATH_VERSICHERUNG_CARDNR}
                    checkValidity={validateInsuranceNumber}
                    required={true}
                />
                <CIS4mDatePicker label={formatMessage(textMap.expirydate)} path={PATH_VERSICHERUNG_EXPIRY_DATE} />
            </CIS4mBlock>

            <CIS4mBlock hidden={hideZusatsversicherungBlock}>
                <Divider />
                <h4>{formatMessage(textMap.zusatzVersicherung)}</h4>
                <CIS4mTextField label={textMap.nameDerVersicherung} path={PATH_ZUSATZVERSICHERUNG_NAME} />
                <CIS4mTextField
                    label={textMap.cardnr}
                    path={PATH_ZUSATZVERSICHERUNG_CARDNR}
                    checkValidity={validateInsuranceNumber}
                    required={true}
                />
                <CIS4mDatePicker label={formatMessage(textMap.expirydate)} path={PATH_ZUSATZVERSICHERUNG_EXPIRY_DATE} />
            </CIS4mBlock>
        </ECheckinSection>
    );
});

Versicherung.displayName = 'Versicherung';

export default Versicherung;
